/**
 * 商談詳細ページのヘッダーカード。
 * Deal の基本属性 + 顧客名 + ステージ/ステータスバッジ。
 *
 * 2026-10: 担当者（社内）・SA ⇄ 紹介者（弊社紹介）・親（所有者）の表示と設定をヘッダー下段に集約 (DealRelationsBar)。
 *
 * 2026-10-30: 入口 (顧客詳細・ダッシュボード・発注・経理・SA 画面) から開いた時は「前の画面へ」を出す (BackToPreviousButton)。
 *   「商談ボードに戻る」は残し、商談ボードを見る権限 (sales.deals) が無い人 (SA など) には出さない。
 *
 * P-H 視覚最適化:
 *  - 戻るリンクを最上部の独立行に分離
 *  - メイン情報を 2 行に集約 (タイトル + サマリ)
 *  - 金額情報を右側に整列、レスポンシブで折り返し
 */
import type { MailContact } from '@goenhub/shared';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  Layers,
  Package,
  Pencil,
  Trash2,
  UserCircle2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { DealRelationsBar } from './DealRelationsBar';

import { BackToPreviousButton } from '@/components/common/BackToPreviousButton';
import { Badge } from '@/components/ui/badge';
import { usePermission } from '@/features/permissions';
import { useAuth } from '@/hooks/useAuth';
import { type DealRecord, useUpdateDeal } from '@/hooks/useDeals';
import { useDeleteDeal } from '@/hooks/useDealUndo';
import { useProductCategoryNameMap } from '@/hooks/useProductCategories';
import { DealStageBadge } from '@/pages/sales-contacts/components/DealStageBadge';
import { paths } from '@/routes/paths';

interface Props {
  deal: DealRecord;
  contact: (MailContact & { id: string }) | null | undefined;
}

export function DealHeader({ deal, contact }: Props) {
  const isBtoB = deal.contactKind === 'btob';
  const isClosed = deal.status === 'closed';

  const update = useUpdateDeal();
  const del = useDeleteDeal();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === 'owner' || user?.role === 'editor';
  const canSeeBoard = usePermission('sales.deals');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(deal.title ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitleDraft(deal.title ?? '');
  }, [deal.title]);

  useEffect(() => {
    if (isEditingTitle) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditingTitle]);

  const handleSaveTitle = async () => {
    const next = titleDraft.trim();
    if (next === (deal.title ?? '').trim()) {
      setIsEditingTitle(false);
      return;
    }
    try {
      await update.mutateAsync({ id: deal.id, title: next });
      toast.success('商談名を更新しました');
      setIsEditingTitle(false);
    } catch (err) {
      toast.error(`更新失敗: ${(err as Error).message}`);
    }
  };

  const handleCancelTitle = () => {
    setTitleDraft(deal.title ?? '');
    setIsEditingTitle(false);
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `商談「${deal.title || '(無題)'}」を削除します。\n` +
          'ゴミ箱へ移動します。30分以内なら即時復元、5営業日以内なら管理者復元が可能です。',
      )
    )
      return;
    try {
      await del.mutateAsync(deal.id);
      toast.success('商談をゴミ箱へ移動しました');
      navigate(paths.deals);
    } catch (err) {
      toast.error(`削除失敗: ${(err as Error).message}`);
    }
  };

  // 主軸カテゴリ表示名解決 (primaryCategoryId → name)。
  // primaryCategoryId が未設定なら primaryCategory (スナップショット) を使う。
  const categoryMap = useProductCategoryNameMap();
  const primaryCategoryName =
    (deal.primaryCategoryId && categoryMap.get(deal.primaryCategoryId)) ||
    deal.primaryCategory ||
    null;

  return (
    <>
      {/* 戻るボタン・リンク + 削除ボタンを独立行に */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <BackToPreviousButton />
          {canSeeBoard && (
            <Link
              to={paths.deals}
              className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--accent)]"
            >
              <ArrowLeft className="size-3" />
              商談ボードに戻る
            </Link>
          )}
        </div>
        {canDelete && (
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={del.isPending}
            title="この商談を削除 (ゴミ箱へ移動)"
            className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--accent-red)] rounded-sm px-1.5 py-1 hover:bg-[var(--bg-hover)] disabled:opacity-50"
          >
            <Trash2 className="size-3.5" />
            削除
          </button>
        )}
      </div>

      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1 space-y-2">
            {/* バッジ行 */}
            <div className="flex items-center gap-2 flex-wrap">
              {isBtoB ? (
                <Building2 className="size-4 text-[var(--accent)]" />
              ) : (
                <UserCircle2 className="size-4 text-[var(--accent-purple)]" />
              )}
              {contact ? (
                <Link
                  to={paths.salesContactDetail.replace(':contactId', contact.id)}
                  className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--accent)]"
                >
                  {contact.name || '(無名)'}
                </Link>
              ) : (
                <span className="text-sm font-medium text-[var(--text-muted)]">
                  顧客 {deal.contactId}
                </span>
              )}
              <span className="text-[var(--text-muted)]">·</span>
              <Badge variant={isBtoB ? 'accent' : 'review'}>{isBtoB ? 'BtoB' : 'BtoC'}</Badge>
              <DealStageBadge stage={deal.stage} />
              {isClosed && <Badge variant="default">closed</Badge>}
              {deal.lostReason && <Badge variant="danger">理由: {deal.lostReason}</Badge>}
              {/* 主軸カテゴリ (primaryCategoryId → name)。
                  この商談がどのカテゴリ起点で生まれた / 紐づいているかを明示。 */}
              {primaryCategoryName && (
                <Badge variant="default" title={`主軸カテゴリ: ${primaryCategoryName}`}>
                  <span className="inline-flex items-center gap-1">
                    <Layers className="size-2.5" />
                    {primaryCategoryName}
                  </span>
                </Badge>
              )}
              {/* v3.2.19: 商談作成時に有効だった商品カタログ (価格表バージョン) */}
              {deal.productCatalogSnapshot && deal.productCatalogSnapshot.length > 0 && (
                <>
                  {deal.productCatalogSnapshot.map((s) => (
                    <Badge
                      key={s.catalogId}
                      variant="default"
                      title={`商談作成時の価格表: ${s.brand} / ${s.label} (${s.version})`}
                    >
                      <span className="inline-flex items-center gap-1">
                        <Package className="size-2.5" />
                        {s.brand} {s.label}
                      </span>
                    </Badge>
                  ))}
                </>
              )}
            </div>

            {/* タイトル — クリックでインライン編集 (成約済みは編集不可) */}
            {isEditingTitle ? (
              <div className="flex items-center gap-1.5">
                <input
                  ref={inputRef}
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleSaveTitle();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelTitle();
                    }
                  }}
                  disabled={update.isPending}
                  className="flex-1 min-w-0 px-2 py-1 text-lg font-semibold leading-tight rounded-sm border border-[var(--border-focus)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => void handleSaveTitle()}
                  disabled={update.isPending}
                  className="p-1 rounded-sm text-[var(--accent-green)] hover:bg-[var(--bg-hover)] disabled:opacity-50"
                  title="保存 (Enter)"
                >
                  <Check className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={handleCancelTitle}
                  disabled={update.isPending}
                  className="p-1 rounded-sm text-[var(--text-muted)] hover:bg-[var(--bg-hover)] disabled:opacity-50"
                  title="キャンセル (Esc)"
                >
                  <X className="size-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => !isClosed && setIsEditingTitle(true)}
                disabled={isClosed}
                title={
                  isClosed ? '成約・失注済みの商談は編集できません' : 'クリックして商談名を編集'
                }
                className="group inline-flex items-center gap-1.5 text-left text-lg font-semibold text-[var(--text-primary)] leading-tight rounded-sm px-1 -mx-1 py-0.5 -my-0.5 hover:bg-[var(--bg-hover)] disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <span>{deal.title || '(タイトル未設定)'}</span>
                {!isClosed && (
                  <Pencil className="size-3.5 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                )}
              </button>
            )}

            {/* 顧客サブ情報 (1 行に集約) */}
            {contact && (
              <div className="flex items-center gap-x-3 gap-y-1 text-xs text-[var(--text-muted)] flex-wrap">
                {contact.companyName && (
                  <span>
                    {contact.companyName}
                    {contact.department ? ` / ${contact.department}` : ''}
                  </span>
                )}
                <span>{contact.defaultEmail}</span>
                <Link
                  to={paths.salesContactDetail.replace(':contactId', contact.id)}
                  className="text-[var(--accent)] hover:underline inline-flex items-center gap-0.5"
                >
                  顧客詳細
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            )}
          </div>

          {/* 金額情報 (2026-07-29): 「期待 — / 成約 —」の無意味な 2 列固定をやめ、
              値があるものだけ出す。両方未設定なら概要タブへの導線を 1 行だけ出す。 */}
          <div className="flex items-start gap-6 text-right flex-shrink-0">
            {deal.expectedAmount == null && deal.finalAmount == null ? (
              <div className="text-[11px] text-[var(--text-muted)] pt-1">
                期待金額 未設定
                <span className="block text-[10px]">（概要タブで入力）</span>
              </div>
            ) : (
              <>
                {deal.expectedAmount != null && (
                  <div>
                    <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                      期待
                    </div>
                    <div className="text-sm font-medium text-[var(--text-secondary)] tabular-nums">
                      ¥{deal.expectedAmount.toLocaleString()}
                    </div>
                  </div>
                )}
                {deal.finalAmount != null && (
                  <div>
                    <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                      成約
                    </div>
                    <div className="text-sm font-semibold tabular-nums text-[var(--accent-green)]">
                      ¥{deal.finalAmount.toLocaleString()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 担当者（社内）/ SA ⇄ 紹介者（弊社紹介）/ 親（所有者）— 表示と設定をヘッダーに集約 (2026-10) */}
        <div className="mt-3 pt-3 border-t border-[var(--border)]">
          <DealRelationsBar deal={deal} />
        </div>
      </div>
    </>
  );
}
